import React from 'react';
import axios from 'axios';
import { useInfiniteQuery, useQuery } from 'react-query';
import { format } from 'date-fns';
import Icon from '@mdi/react';
import { mdiEye, mdiFilter, mdiLoading, mdiMagnify } from '@mdi/js';

import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Titulo from '@/components/tipografia/Titulo';
import Descricao from '@/components/tipografia/Descricao';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Grid, { GridItem } from '@/components/layout/Grid';
import { Accordion } from '@/components/layout/Accordion';
import Badge from '@/components/layout/Badge';
import Card from '@/components/layout/Card';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoData from '@/components/Formulario/CampoData';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import { useDebounce } from '@/hooks/useDebounce';

import {
  ClassificacaoDto,
  ClassificacaoResumo,
  ROTULOS_ORIGEM_CLASSIFICACAO,
} from '@tera/shared/src/types/ClassificacaoTexto';

import styles from './page.module.css';

type ResponseList = {
  ok: boolean;
  total: number;
  result: ClassificacaoDto[];
  cursor: number | null;
};

const OPCOES_ORIGEM = Object.entries(ROTULOS_ORIGEM_CLASSIFICACAO).map(
  ([id, label]) => ({ id, label }),
);

const pct = (parte: number, total: number) =>
  total > 0 ? `${Math.round((parte / total) * 100)}%` : '—';

const Resumo = ({ r }: { r: ClassificacaoResumo }) => (
  <div className={styles.cards}>
    <Card>
      <div className={styles.cardTitulo}>Classificações</div>
      <div className={styles.cardValor}>{r.total}</div>
      <div className={styles.cardSub}>no período filtrado</div>
    </Card>
    <Card>
      <div className={styles.cardTitulo}>Decididas localmente</div>
      <div className={styles.cardValor}>{pct(r.laya, r.total)}</div>
      <div className={styles.cardSub}>{r.laya} pelo Laya, sem LLM</div>
    </Card>
    <Card>
      <div className={styles.cardTitulo}>Enviadas ao LLM</div>
      <div className={styles.cardValor}>{pct(r.llm, r.total)}</div>
      <div className={styles.cardSub}>
        {r.llm} abaixo do limiar
        {r.laya_indisponivel > 0 ? ` · ${r.laya_indisponivel} com Laya indisponível` : ''}
      </div>
    </Card>
    <Card>
      <div className={styles.cardTitulo}>Laya teria acertado</div>
      <div className={styles.cardValor}>
        {pct(r.concordancia.iguais, r.concordancia.total)}
      </div>
      <div className={styles.cardSub}>
        {r.concordancia.iguais} de {r.concordancia.total} fallbacks em que a melhor
        opção do Laya coincidiu com o LLM
      </div>
    </Card>
  </div>
);

const Page = () => {
  const [detalhe, setDetalhe] = React.useState<ClassificacaoDto | null>(null);

  const [dataIni, setDataIni] = React.useState('');
  const [dataFim, setDataFim] = React.useState('');
  const [origem, setOrigem] = React.useState<string | null>(null);
  const [busca, setBusca] = React.useState('');
  const buscaDebounced = useDebounce(busca, 500);

  const params = React.useMemo(() => {
    const p = new URLSearchParams();
    if (dataIni) p.set('di', dataIni);
    if (dataFim) p.set('df', dataFim);
    if (origem) p.set('o', origem);
    if (buscaDebounced) p.set('txt', buscaDebounced);
    return p;
  }, [dataIni, dataFim, origem, buscaDebounced]);

  const queryResumo = useQuery({
    queryKey: ['ia-classificacao-resumo', params.toString()],
    queryFn: async () => {
      const { data } = await axios.get<{ result: ClassificacaoResumo }>(
        `/api/ia_classificacao/resumo?${params.toString()}`,
        { withCredentials: true },
      );
      return data.result;
    },
  });

  const queryList = useInfiniteQuery({
    queryKey: ['ia-classificacao', params.toString()],
    queryFn: async ({ pageParam }) => {
      const p = new URLSearchParams(params);
      if (pageParam) p.set('c', String(pageParam));
      const { data } = await axios.get<ResponseList>(
        `/api/ia_classificacao?${p.toString()}`,
        { withCredentials: true },
      );
      return data;
    },
    getNextPageParam: lastPage => lastPage.cursor ?? undefined,
  });

  const linhas = queryList.data?.pages?.flatMap(p => p.result) ?? [];
  const total =
    queryList.data?.pages?.[queryList.data.pages.length - 1]?.total ?? 0;

  const headers: DataTableHeader[] = [
    {
      label: 'Quando',
      width: '150px',
      render: (row: ClassificacaoDto) =>
        format(new Date(row.data_criacao), 'dd/MM/yyyy HH:mm:ss'),
    },
    {
      label: 'Mensagem',
      width: '35%',
      render: (row: ClassificacaoDto) => (
        <div className={styles.registro}>
          <span className={styles.texto}>{row.input_anonimizado}</span>
          {row.chat_id != null && (
            <span className={styles.registroId}>chat #{row.chat_id}</span>
          )}
        </div>
      ),
    },
    {
      label: 'Resultado',
      width: '180px',
      render: (row: ClassificacaoDto) => (
        <div className={styles.registro}>
          <span>{row.output.result}</span>
          <span className={styles.registroId}>
            {row.output.opcoes.length} opções
          </span>
        </div>
      ),
    },
    {
      label: 'Decidido por',
      width: '120px',
      render: (row: ClassificacaoDto) => (
        <Badge variant={row.output.origem === 'laya' ? 'success' : 'warning'}>
          {ROTULOS_ORIGEM_CLASSIFICACAO[row.output.origem]}
        </Badge>
      ),
    },
    {
      label: 'Pontuação',
      width: '110px',
      render: (row: ClassificacaoDto) =>
        row.output.laya ? row.output.laya.pontuacao.toFixed(2) : '—',
    },
    {
      label: 'Laya sugeriu',
      width: '160px',
      render: (row: ClassificacaoDto) => {
        const laya = row.output.laya;
        if (!laya) return <span className={styles.registroId}>indisponível</span>;
        if (row.output.origem === 'laya') return '';
        const acertou = laya.melhor_opcao === row.output.result;
        return (
          <div className={styles.registro}>
            <span>{laya.melhor_opcao}</span>
            <span className={styles.registroId}>
              {acertou ? 'igual ao LLM' : 'diferente do LLM'}
            </span>
          </div>
        );
      },
    },
    {
      label: 'Detalhes',
      width: '90px',
      stickRight: true,
      alignRight: true,
      render: (row: ClassificacaoDto) => (
        <Botao
          variant="none-primary"
          icon={mdiEye}
          title="Ver detalhes"
          onClick={() => setDetalhe(row)}
        />
      ),
    },
  ];

  const probs = Object.entries(detalhe?.output.laya?.probabilidades ?? {}).sort(
    (a, b) => b[1] - a[1],
  );

  return (
    <LayoutUsuario>
      <Modal
        instanceId="modal-ia-classificacao-detalhe"
        title="Detalhe da classificação"
        show={!!detalhe}
        onClose={() => setDetalhe(null)}
        onCancel={() => setDetalhe(null)}
      >
        {detalhe && (
          <VBox gap="12px">
            <div className={styles.meta}>
              {format(new Date(detalhe.data_criacao), 'dd/MM/yyyy HH:mm:ss')}
              {detalhe.chat_id != null ? ` · chat #${detalhe.chat_id}` : ''} ·
              decidido por {ROTULOS_ORIGEM_CLASSIFICACAO[detalhe.output.origem]}
            </div>

            <div>
              <div className={styles.cardTitulo}>Mensagem (anonimizada)</div>
              <div className={styles.bloco}>{detalhe.input_anonimizado}</div>
            </div>

            <div>
              <div className={styles.cardTitulo}>Critério</div>
              <div className={styles.bloco}>{detalhe.output.criterio}</div>
            </div>

            <div>
              <div className={styles.cardTitulo}>Resultado</div>
              <div>
                <strong>{detalhe.output.result}</strong>
                {detalhe.output.llm && detalhe.output.laya && (
                  <span className={styles.registroId}>
                    {' '}
                    — Laya devolveu unknown (pontuação{' '}
                    {detalhe.output.laya.pontuacao.toFixed(3)}, confiança{' '}
                    {detalhe.output.laya.confianca.toFixed(3)})
                  </span>
                )}
              </div>
            </div>

            {probs.length > 0 ? (
              <table className={styles.probs}>
                <thead>
                  <tr>
                    <th>Opção</th>
                    <th></th>
                    <th>Probabilidade</th>
                  </tr>
                </thead>
                <tbody>
                  {probs.map(([opcao, p]) => (
                    <tr key={opcao}>
                      <td
                        className={
                          opcao === detalhe.output.result ? styles.escolhida : ''
                        }
                      >
                        {opcao}
                      </td>
                      <td style={{ width: '40%' }}>
                        <span
                          className={styles.barra}
                          style={{ width: `${Math.round(p * 100)}%` }}
                        />
                      </td>
                      <td>{(p * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Descricao>
                O classificador local não respondeu; a decisão foi direto para o LLM.
              </Descricao>
            )}
          </VBox>
        )}
      </Modal>

      <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
        {/* div, não VBox: .vbox tem height 100% e, como filho flex do container
            de altura fixa, o cabeçalho reservaria a tela inteira. */}
        <div>
          <Titulo>Classificador de texto</Titulo>
          <Descricao>
            Quando o cliente responde em texto livre a um menu do chatbot, o
            classificador local (Laya) tenta decidir sozinho; abaixo do limiar
            de confiança a decisão vai para o LLM. Aqui dá para ver o que ficou
            local, o que foi para o LLM e se o Laya teria acertado. Os registros
            são mantidos por 180 dias.
          </Descricao>
        </div>

        {queryResumo.data && <Resumo r={queryResumo.data} />}

        <HBox>
          <CampoTexto
            prepend={
              <Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-500)" />
            }
            placeholder="Pesquisar na mensagem"
            value={busca}
            onChange={setBusca}
          />
        </HBox>

        <Accordion
          title={
            <HBox gap="8px">
              <Icon path={mdiFilter} size={1} />
              Filtros
            </HBox>
          }
          startOpen={false}
        >
          <Grid>
            <GridItem sm={12} md={3}>
              <CampoData
                type="datetime-local"
                label="De"
                value={dataIni}
                onChange={setDataIni}
              />
            </GridItem>
            <GridItem sm={12} md={3}>
              <CampoData
                type="datetime-local"
                label="Até"
                value={dataFim}
                onChange={setDataFim}
              />
            </GridItem>
            <GridItem sm={12} md={6}>
              <CampoSeletor
                nullable
                label="Decidido por"
                placeholder="(Qualquer)"
                value={origem}
                onChange={setOrigem}
                options={OPCOES_ORIGEM}
              />
            </GridItem>
          </Grid>
        </Accordion>

        {queryList.isFetching ? (
          <div>
            Aguarde...{' '}
            <Icon
              path={mdiLoading}
              size="14px"
              color="var(--tc-color-gray-700)"
              spin
            />
          </div>
        ) : (
          <div>
            Exibindo {linhas.length} de {total} registros.
          </div>
        )}

        <DataTable
          isLoading={queryList.isFetching}
          cabecalhos={headers}
          linhas={linhas}
          hasMoreData={queryList.hasNextPage}
          onLoadMore={async () => queryList.fetchNextPage()}
        />
      </VBox>
    </LayoutUsuario>
  );
};

export default Page;
